/**
 * Runtime validation schemas for API responses — the boundary guards that
 * back the hand-written types in `types.ts`. Passed to `apiRequest`'s `schema`
 * arg so a drifted/malformed response throws at the boundary instead of
 * flowing through as a bad cast.
 *
 * Design: LENIENT on purpose. Object schemas `.passthrough()` and most fields
 * are optional, so a benign server addition never rejects a valid response
 * (critical for the auth gate — a too-strict schema would brick the portal).
 * We validate only the fields the app actually gates on; a genuinely broken
 * response (e.g. no `role`) is what throws.
 */
import { z } from 'zod';

/** `/api/me` — gates portal access (roles) and assistant access (effectiveScopes). */
export const meResponseSchema = z
  .object({
    user: z
      .object({
        id: z.string(),
        email: z.string(),
        name: z.string().nullable(),
        /**
         * OPTIONAL on purpose. This validator runs against a DEPLOYED API that
         * may be a version ahead or behind: requiring a field the server just
         * stopped sending throws at parse time and renders as "not signed in",
         * which is exactly what happened when `role` became `roles`. The app
         * gates on `permissions` and `effectiveScopes`; an absent roles list
         * must degrade, never brick.
         */
        roles: z.array(z.string()).optional(),
        permissions: z.array(z.string()).optional(),
        scopes: z.array(z.string()).optional(),
        effectiveScopes: z.array(z.string()).optional(),
      })
      .passthrough(),
    tenant: z
      .object({
        id: z.string(),
        slug: z.string(),
        name: z.string(),
      })
      .passthrough(),
    fixtureMode: z.boolean().optional(),
  })
  .passthrough();

// --- Usage (GET /api/usage, /api/usage/conversations) ---------------------
// The page renders every one of these fields, so a response missing one (an API a
// version behind) should fail here as a clear error, not crash a card mid-render.

const usd = z.number();
const nullableNumber = z.number().nullable();

const usageTotalsSchema = z
  .object({
    turns: z.number(),
    activeUsers: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    totalTokens: z.number(),
    cachedInputTokens: z.number(),
    estimatedCostUsd: usd,
    costPerAnswerUsd: nullableNumber,
    cacheShare: nullableNumber,
  })
  .passthrough();

const usageTrendRowSchema = z
  .object({
    date: z.string(),
    turns: z.number(),
    totalTokens: z.number(),
    activeUsers: z.number(),
    costPerAnswerUsd: nullableNumber,
    cacheShare: nullableNumber,
  })
  .passthrough();

const modelPricingSchema = z
  .object({
    model: z.string(),
    inputPerMTokUsd: z.number(),
    outputPerMTokUsd: z.number(),
    cacheReadPerMTokUsd: z.number(),
    cacheWritePerMTokUsd: z.number(),
  })
  .passthrough();

export const usageSummaryResponseSchema = z
  .object({
    since: z.string(),
    until: z.string(),
    totals: usageTotalsSchema,
    previous: z.object({ totals: usageTotalsSchema }).passthrough(),
    byTool: z.array(
      z
        .object({ toolName: z.string(), count: z.number(), avgResultTokens: nullableNumber })
        .passthrough(),
    ),
    byUser: z.array(
      z
        .object({
          userId: z.string().nullable(),
          label: z.string(),
          turns: z.number(),
          totalTokens: z.number(),
          estimatedCostUsd: usd,
          costPerTurnUsd: nullableNumber,
          fromCacheShare: nullableNumber,
          last7DaysUsd: z.array(z.number()),
        })
        .passthrough(),
    ),
    byDay: z.array(usageTrendRowSchema),
    byHour: z.array(usageTrendRowSchema),
    spend: z
      .object({
        byModel: z.array(
          z
            .object({
              model: z.string(),
              calls: z.number(),
              costUsd: usd,
              pricing: modelPricingSchema.nullable(),
            })
            .passthrough(),
        ),
        bySourcePerDay: z.array(
          z.object({ date: z.string(), source: z.string(), costUsd: usd }).passthrough(),
        ),
        makeup: z
          .object({
            freshInputUsd: usd,
            cacheReadUsd: usd,
            cacheWriteUsd: usd,
            outputUsd: usd,
            tokens: z
              .object({
                freshInput: z.number(),
                cacheRead: z.number(),
                cacheWrite: z.number(),
                output: z.number(),
              })
              .passthrough(),
          })
          .passthrough(),
      })
      .passthrough(),
    month: z
      .object({
        month: z.string(),
        dayOfMonth: z.number(),
        daysInMonth: z.number(),
        spentUsd: usd,
        projectedUsd: usd,
        previousMonthUsd: usd,
        previousMonthToDateUsd: usd,
      })
      .passthrough(),
  })
  .passthrough();

export const usageConversationsResponseSchema = z
  .object({
    conversations: z.array(
      z
        .object({
          conversationId: z.string(),
          title: z.string().nullable(),
          userLabel: z.string(),
          turns: z.number(),
          costUsd: usd,
          costPerTurnUsd: nullableNumber,
          fromCacheShare: nullableNumber,
          lastActiveAt: z.string(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

/** `/api/usage/conversations/:id` — one conversation's cost, turn by turn. */
export const usageConversationDetailResponseSchema = z
  .object({
    conversationId: z.string(),
    title: z.string().nullable(),
    userLabel: z.string(),
    startedAt: z.string(),
    lastActiveAt: z.string(),
    models: z.array(z.string()),
    turns: z.number(),
    costUsd: usd,
    costPerTurnUsd: nullableNumber,
    fromCacheShare: nullableNumber,
    tenantCostPerTurnUsd: nullableNumber,
    summaries: z.array(z.object({ at: z.string(), costUsd: usd }).passthrough()),
    turnRows: z.array(
      z
        .object({
          turnId: z.string(),
          at: z.string(),
          tools: z.array(z.object({ toolName: z.string(), count: z.number() }).passthrough()),
          steps: nullableNumber,
          cacheReadTokens: z.number(),
          cacheWriteTokens: z.number(),
          outputTokens: z.number(),
          costUsd: usd,
          costParts: z
            .object({
              freshInputUsd: usd,
              cacheReadUsd: usd,
              cacheWriteUsd: usd,
              outputUsd: usd,
            })
            .passthrough()
            .nullable(),
        })
        .passthrough(),
    ),
    truncated: z.boolean(),
  })
  .passthrough();
